/**
 * Unsaved work, per section. A page holding input that isn't saved yet registers it while it is
 * dirty; the shell asks before choosing that section in the menu would close the page, and the
 * browser asks before the tab is closed. Nothing here saves or discards by itself.
 */
import { createContext, useContext, useEffect } from 'react';

export const SectionContext = createContext<string>('');
const holders = new Map<symbol, string>();
const onUnload = (e: BeforeUnloadEvent) => { if (holders.size) { e.preventDefault(); e.returnValue = ''; } };

/** While `dirty`, the surrounding section counts as having unsaved work. */
export function useUnsaved(dirty: boolean) {
  const section = useContext(SectionContext);
  useEffect(() => {
    if (!dirty) return;
    const key = Symbol(section);
    holders.set(key, section);
    if (holders.size === 1) addEventListener('beforeunload', onUnload);
    return () => { holders.delete(key); if (!holders.size) removeEventListener('beforeunload', onUnload); };
  }, [dirty, section]);
}
export const hasUnsaved = (section: string) => [...holders.values()].includes(section);
/** Unsaved work anywhere in the workspace (leaving the workspace would drop it). */
export const hasAnyUnsaved = () => holders.size > 0;
/** The person already confirmed losing unsaved work (Restart anyway): the browser doesn't ask again. */
export const stopAskingBeforeLeaving = () => removeEventListener('beforeunload', onUnload);
